import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';

// What a section calls itself: its own name, else its kind; null for none.
const sectionWords = (section) =>
  roomDisplayName(section.name) ??
  (section.label !== 'unidentified' ? roomDisplayName(section.label) : null);

/**
 * The words on one room label, for one anchor `chooseRoomLabelAnchors`
 * returned (`@shared-lib/geometry/roomLabelAnchor`). The floor plan
 * (`drawRoomLabels`), the minimap and the 3D labels (`RoomLabelWithSections`)
 * all call this, so every view shows the same string for the same anchor.
 *
 *   - a named room with several sections: "Name:Section" per section
 *   - an unnamed room's section: the section's own name, else its kind
 *   - otherwise: the room's name
 *
 * Every name and kind reads the way a person writes it (`roomDisplayName`:
 * "livingRoom" → "Living room"). RoomPlan's `unidentified` placeholder names
 * nothing. Each view keeps its own words for a label nothing names, passed as
 * `unnamedSection` (an unnamed room's unidentified section) and `unnamedRoom`.
 *
 * @param {object} room
 * @param {object|null} section  the anchor's section, null for a room label
 * @param {{unnamedSection: string, unnamedRoom: string}} fallbacks
 * @returns {string}
 */
export const roomAnchorLabelText = (
  room,
  section,
  { unnamedSection, unnamedRoom }
) => {
  const name = roomDisplayName(room?.name);
  if (section && name && room.sections?.length > 1) {
    return `${name}:${sectionWords(section) ?? 'Area'}`;
  }
  if (section && !name) return sectionWords(section) ?? unnamedSection;
  return name ?? roomDisplayName(room?.label || room?.category) ?? unnamedRoom;
};
